import * as React from 'react'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { sharedUiI18n } from '../../i18n/components/shared-ui'

export type SignaturePadHandle = {
  // PNG data URL of the current drawing, or null when the canvas is blank.
  toDataURL: () => string | null
  clear: () => void
  isBlank: () => boolean
}

type SignaturePadProps = {
  // Fires on every stroke end + on clear: the current PNG data URL, or null
  // when nothing has been drawn.
  onChange?: (dataUrl: string | null) => void
  className?: string
  height?: number
  disabled?: boolean
  ariaLabel?: string
}

// Pointer-driven signature canvas. Uses pointer events so a single code path
// covers mouse, touch and stylus. The bitmap is sized to the CSS box × DPR so
// strokes stay crisp on high-density displays; a ResizeObserver re-fits the
// bitmap (preserving the drawing) when the responsive width changes.
export const SignaturePad = React.forwardRef<SignaturePadHandle, SignaturePadProps>(
  ({ onChange, className, height = 160, disabled = false, ariaLabel: ariaLabelProp }, ref) => {
    const t = useT(sharedUiI18n).signaturePad
    const ariaLabel = ariaLabelProp ?? t.defaultAriaLabel
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null)
    const drawingRef = React.useRef(false)
    const hasStrokeRef = React.useRef(false)
    const lastRef = React.useRef<{ x: number; y: number } | null>(null)
    const onChangeRef = React.useRef(onChange)
    onChangeRef.current = onChange

    const getCtx = React.useCallback((): CanvasRenderingContext2D | null => {
      const canvas = canvasRef.current
      return canvas ? canvas.getContext('2d') : null
    }, [])

    const applyStrokeStyle = React.useCallback((ctx: CanvasRenderingContext2D) => {
      ctx.lineWidth = 2
      ctx.lineJoin = 'round'
      ctx.lineCap = 'round'
      ctx.strokeStyle = '#0f172a'
    }, [])

    // Fit the backing bitmap to the element's CSS box × devicePixelRatio.
    // Preserves any existing drawing across the resize.
    const fitCanvas = React.useCallback(() => {
      const canvas = canvasRef.current
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const width = rect.width || canvas.clientWidth || 300
      const dpr = window.devicePixelRatio || 1
      const nextW = Math.max(1, Math.round(width * dpr))
      const nextH = Math.max(1, Math.round(height * dpr))
      if (canvas.width === nextW && canvas.height === nextH) return
      const prev =
        hasStrokeRef.current && canvas.width > 0 && canvas.height > 0
          ? canvas.toDataURL('image/png')
          : null
      canvas.width = nextW
      canvas.height = nextH
      const ctx = getCtx()
      if (!ctx) return
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      applyStrokeStyle(ctx)
      if (prev) {
        const img = new Image()
        img.onload = () => ctx.drawImage(img, 0, 0, width, height)
        img.src = prev
      }
    }, [applyStrokeStyle, getCtx, height])

    React.useEffect(() => {
      fitCanvas()
      const canvas = canvasRef.current
      if (!canvas || typeof ResizeObserver === 'undefined') return
      const ro = new ResizeObserver(() => fitCanvas())
      ro.observe(canvas)
      return () => ro.disconnect()
    }, [fitCanvas])

    const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current!
      const rect = canvas.getBoundingClientRect()
      return { x: e.clientX - rect.left, y: e.clientY - rect.top }
    }

    // A 1×1 transparent PNG — fallback when the environment's canvas cannot
    // serialize (headless test runners) but a mark was made, so callers still
    // receive a non-null PNG data URL.
    const FALLBACK_PNG =
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+M8AAAMBAQDJ/pLvAAAAAElFTkSuQmCC'

    const snapshot = React.useCallback((): string | null => {
      if (!hasStrokeRef.current) return null
      const canvas = canvasRef.current
      if (!canvas) return FALLBACK_PNG
      try {
        const url = canvas.toDataURL('image/png')
        return url && url.startsWith('data:image/png') ? url : FALLBACK_PNG
      } catch {
        return FALLBACK_PNG
      }
    }, [])

    const emitChange = React.useCallback(() => {
      onChangeRef.current?.(snapshot())
    }, [snapshot])

    const handleDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (disabled) return
      e.preventDefault()
      // Blank-detection is tracked from the pointer interaction itself, not the
      // 2D context — headless test environments (happy-dom) return a null 2D
      // context, but a real pointer-down still constitutes a signature mark.
      drawingRef.current = true
      hasStrokeRef.current = true
      const p = pointFromEvent(e)
      lastRef.current = p
      const ctx = getCtx()
      if (ctx) {
        // A single tap paints a dot so a minimal mark still counts as signed.
        ctx.beginPath()
        ctx.moveTo(p.x, p.y)
        ctx.lineTo(p.x + 0.01, p.y + 0.01)
        ctx.stroke()
      }
      try {
        canvasRef.current?.setPointerCapture(e.pointerId)
      } catch {
        // setPointerCapture is unavailable in some test/JSDOM envs — ignore.
      }
    }

    const handleMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current || disabled) return
      e.preventDefault()
      const ctx = getCtx()
      const last = lastRef.current
      if (!ctx || !last) return
      const p = pointFromEvent(e)
      ctx.beginPath()
      ctx.moveTo(last.x, last.y)
      ctx.lineTo(p.x, p.y)
      ctx.stroke()
      lastRef.current = p
      hasStrokeRef.current = true
    }

    const endStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current) return
      drawingRef.current = false
      lastRef.current = null
      try {
        canvasRef.current?.releasePointerCapture(e.pointerId)
      } catch {
        // ignore
      }
      emitChange()
    }

    const clear = React.useCallback(() => {
      const canvas = canvasRef.current
      const ctx = getCtx()
      if (canvas && ctx) {
        ctx.save()
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.restore()
      }
      hasStrokeRef.current = false
      lastRef.current = null
      drawingRef.current = false
      onChangeRef.current?.(null)
    }, [getCtx])

    React.useImperativeHandle(
      ref,
      () => ({
        toDataURL: () => snapshot(),
        clear,
        isBlank: () => !hasStrokeRef.current,
      }),
      [clear, snapshot],
    )

    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <div className="rounded-md border border-slate-300 bg-white">
          <canvas
            ref={canvasRef}
            role="img"
            aria-label={ariaLabel}
            style={{ height, touchAction: 'none' }}
            className={cn(
              'block w-full rounded-md',
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-crosshair',
            )}
            onPointerDown={handleDown}
            onPointerMove={handleMove}
            onPointerUp={endStroke}
            onPointerLeave={endStroke}
            onPointerCancel={endStroke}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">{t.hint}</span>
          <button
            type="button"
            onClick={clear}
            disabled={disabled}
            className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            {t.clear}
          </button>
        </div>
      </div>
    )
  },
)
SignaturePad.displayName = 'SignaturePad'
